import { useDispatch, useSelector } from "react-redux";
import {
  selectFilterTagsList,
  selectInscribedSubjects,
  selectOfferedSubjects,
  selectSearchSubjectsValue,
  selectSelectedFilterTagsList,
  selectSubcriptionProcessSectionIds,
  selectSubjectsQuotas,
  setSelectedFilterTagsList,
} from "../../reduxSlices/subjectsSlice";
import OfferSubjectsVirtualizedList from "../OfferSubjectsVirtualizedList/OfferSubjectsVirtualizedList";
import OfferOnProcess from "../OfferOnProcess/OfferOnProcess";
import { useEffect, useMemo, useRef, useState } from "react";
import { VariableSizeList as List } from "react-window";
import { IOfferedSubject } from "../../types";
import DesktopTab from "../DesktopTab/DesktopTab";
import SearchInput from "../SearchInput/SearchInput";
import { NavOptions } from "../NavOptions/NavOptions";
import {
  selectMobileFeatures,
  selectVerFijadas,
  setCardsCollapse,
  setPanelFilters,
  setVerFijadas,
} from "../../reduxSlices/globalFlagsSlice";
import DesktopSubjectItem from "../DesktopSubjectItem/DesktopSubjectItem";
import { BsFillPinAngleFill } from "react-icons/bs";
import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import Translatable from "../Translatable/Translatable";
import SlSkeleton from "@shoelace-style/shoelace/dist/react/skeleton";
import { getCourseQuota } from "../../utils/quota";
import usePinnedSubjects from "../../hooks/usePinnedSubjects";
import useSubjectSearchTranslations from "../../hooks/useSubjectSearchTranslations";
import { filterSubjects } from "../../utils/subjects";

const filterSubjectsByTag = (subjects: IOfferedSubject[], filterTagsList: string[]) => {
  if (!filterTagsList.length) {
    console.log("filterSubjectsByTag - subjects", subjects.length);
    return subjects;
  }
  const subjectsByFilter = subjects
    .map((s: IOfferedSubject) => {
      const filteredSections = s.sections.filter((section) => {
        // Verificar que 'tag' esté definido y sea una cadena
        if (typeof section.tag !== "string") {
          return false;
        }
        // Dividir la cadena 'tag' y limpiar los espacios
        const tags = section.tag.split(",").map((item) => item.trim());
        // Verificar si algún elemento de 'tags' está en 'filterTagsList'
        // console.log(tags.some((item) => filterTagsList.includes(item)));
        return tags.some((item) => filterTagsList.includes(item));
      });

      return {
        ...s,
        sections: filteredSections,
      };
    })
    .filter((s: IOfferedSubject) => s.sections.length > 0);

  return subjectsByFilter;
};

export default function OfferVirtualListContainer() {
  const dispatch = useDispatch();
  const offeredSubjects = useSelector(selectOfferedSubjects);
  const subjectsQuotas = useSelector(selectSubjectsQuotas);
  const selectedFilterTagsListStore = useSelector(selectSelectedFilterTagsList);
  const searchSubjectValue = useSelector(selectSearchSubjectsValue);
  const inscribedSubjects = useSelector(selectInscribedSubjects);
  const filterTagsList = useSelector(selectFilterTagsList);
  const mobileFeatures = useSelector(selectMobileFeatures);
  const verFijadas = useSelector(selectVerFijadas);
  const subcriptionProcessSectionIds = useSelector(selectSubcriptionProcessSectionIds);

  const { pinnedSubjectsList, pinnedNotInscribedCount, isCoursePinned, isCourseInProcess } = usePinnedSubjects();
  const titleTranslations = useSubjectSearchTranslations(offeredSubjects);

  const virtualListRef = useRef<List>(null);
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  const [filteredSubjects, setFilteredSubjects] = useState<IOfferedSubject[]>([]);
  const [subjectsInSearch, setSubjectsInSearch] = useState<IOfferedSubject[]>([]);
  const [filtro, setFiltro] = useState("OBLIGATORIO");
  const [scrollPosition, setScrollPosition] = useState(0);
  const [scrollPositionVirtualList, setScrollPositionVirtualList] = useState(0);

  // Usar el contador del hook
  const pinnedNotFilterCount = pinnedNotInscribedCount;

  useEffect(() => {
    if (offeredSubjects && offeredSubjects.length >= 0) {
      const result = offeredSubjects.filter((s) => s.tipoMateria === filtro);
      setFilteredSubjects(result);
    } else {
    }
  }, [offeredSubjects, filtro]);

  useEffect(() => {
    if (pinnedNotInscribedCount === 0 && verFijadas) {
      dispatch(setVerFijadas(false));
    }
  }, [pinnedNotInscribedCount, verFijadas]);

  /* -------------------------------- Busqueda + Filtros---------------------- */
  useEffect(() => {
    if (filteredSubjects && searchSubjectValue.trim()?.length > 0) {
      const newSubjects = filterSubjects(filteredSubjects, searchSubjectValue, titleTranslations);
      const newSubjectsWithFilter = filterSubjectsByTag(newSubjects, selectedFilterTagsListStore || []);
      setSubjectsInSearch(newSubjectsWithFilter);
    } else if (filteredSubjects) {
      const newSubjects = filterSubjectsByTag(filteredSubjects, selectedFilterTagsListStore || []);
      setSubjectsInSearch(newSubjects);
    }
  }, [searchSubjectValue, selectedFilterTagsListStore, filteredSubjects, titleTranslations]);

  /* --------------------------------- handler -------------------------------- */

  const handleSelectTab = (tipo: string) => {
    if (tipo === "OBLIGATORIO") {
      dispatch(setSelectedFilterTagsList([]));
      dispatch(setPanelFilters(false));
    }
    if (tipo) {
      setFiltro(tipo);
    } else {
      setFiltro("OBLIGATORIO");
    }
  };

  const handleScrollVirtualList = (data: any) => {
    setScrollPositionVirtualList(Number(data.scrollOffset));
  };

  const handleScroll = () => {
    setScrollPosition(Number(scrollTargetRef.current?.scrollTop));
  };

  const scrollVirtualListToTop = () => {
    if (virtualListRef.current) {
      virtualListRef.current.scrollTo(0);
    }
  };

  const handleOpenFilter = () => {
    dispatch(setPanelFilters(true));
  };

  const pinnedSubjectsContent = useMemo(() => {
    if (!offeredSubjects?.length) return null;
    const inscribedSubsIds = inscribedSubjects?.map((s) => ({ idCurso: s.idCurso, anioLectivo: s.anioLectivo })) || [];
    return (
      <div className="w-full flex flex-col">
        {offeredSubjects.map((s) => {
          const pinnedSections = s.sections.filter(
            (curso) =>
              curso.visibleEnOferta &&
              isCoursePinned(curso.idCurso, curso.anioLectivo) &&
              !inscribedSubsIds.some((sub) => sub.idCurso === curso.idCurso && sub.anioLectivo === curso.anioLectivo) &&
              !isCourseInProcess(subcriptionProcessSectionIds, curso.idCurso, curso.anioLectivo)
          );
          return pinnedSections.map((section) => {
            const storeQuota = getCourseQuota(subjectsQuotas, section.subjectData?.reserveData?.idC);
            const quota = { ...storeQuota };
            return (
              <DesktopSubjectItem
                idMateria={section.idMateria}
                id={section.idCurso}
                key={`${section.idCurso}-${quota.quotaId ?? -1}-${quota.availability ?? 0}-${quota.qty ?? 0}`}
                subjectId={section.idMateria}
                section={section.section}
                subjectData={section.subjectData}
                quota={quota}
                pinned={isCoursePinned(section.idCurso, section.anioLectivo)}
                creditos={section.creditos}
                sectionType={section.tipoMateria}
                turno={section.turno}
                subject={section}
                disableAnimations={true}
              />
            );
          });
        })}
      </div>
    );
  }, [offeredSubjects, subjectsQuotas, inscribedSubjects, isCoursePinned, subcriptionProcessSectionIds]);

  const css = `
  .skeleton sl-skeleton{
    --border-radius: 8px;
  }
`;

  return (
    <div className="flex-grow flex flex-col overflow-hidden">
      <div id="titulos" className="flex items-center justify-between mb-[6px] min-h-[40px] h-[40px]">
        {verFijadas ? (
          <h2 className="text-[#908C8C] font-bold text-[18px] flex items-center">
            {/* <BsFillPinAngleFill className="mr-2 text-[20px] text-[#00998C]" /> */}
            <Translatable as="span" capitalize>Cursos fijados:</Translatable>
            <span className="text-[#00C4B3] ml-2">{pinnedNotFilterCount}</span>
          </h2>
        ) : (
          <h2 className="text-[#908C8C] font-bold text-[18px] flex items-center">
            <Translatable as="span" capitalize>Oferta de cursos</Translatable>
          </h2>
        )}
        {verFijadas ? (
          <h2
            className="text-[#908C8C] text-[18px] cursor-pointer hover:underline"
            onClick={() => dispatch(setVerFijadas(false))}
            style={mobileFeatures ? { fontSize: "16px" } : {}}
          >
            <Translatable as="span" capitalize>Ver todos</Translatable>
          </h2>
        ) : (
          <h2
            className={`${
              pinnedNotFilterCount > 0 ? "bg-[#00C4B3] border-none cursor-pointer text-[#fff]" : ""
            } text-[#908C8C] text-[18px] border-[1px] rounded-full px-3 py-1 flex items-center`}
            onClick={() => {
              if (pinnedNotFilterCount > 0) {
                dispatch(setVerFijadas(!verFijadas));
                dispatch(setCardsCollapse("none"));
              }
            }}
          >
            {mobileFeatures ? (
              <BsFillPinAngleFill
                className={`mr-2 text-[14px] ${pinnedNotFilterCount > 0 ? "text-[#fff]" : "text-[#908C8C]"} `}
              />
            ) : (
              <Translatable as="span" capitalize>Cursos fijados:</Translatable>
            )}
            <span className="font-semibold ml-1">{pinnedNotFilterCount}</span>
          </h2>
        )}
      </div>

      {!verFijadas && (
        <div className="desktop-filters-nav relative">
          <DesktopTab filtro={filtro} handleSelectTab={handleSelectTab} />
          <div className="search-and-filter">
            <SearchInput />
            {filtro !== "OBLIGATORIO" && filterTagsList && filterTagsList?.length > 0 && (
              <button
                onClick={handleOpenFilter}
                className={`filters-btn ${selectedFilterTagsListStore?.length ? "filters-btn-active" : ""}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none">
                  <path
                    d="M14.9814 0H1.01854C0.159386 0 -0.31238 1.04052 0.233792 1.73083L5.9521 8.95821C6.10199 9.14766 6.18396 9.38545 6.18396 9.63088V15.7943C6.18396 16.1287 6.33583 16.4434 6.59355 16.6431L8.07226 17.7887C8.7429 18.3083 9.69589 17.8101 9.69589 16.9399V9.63676C9.69589 9.38776 9.78025 9.14674 9.93414 8.95609L15.7598 1.73882C16.3155 1.05041 15.8453 0 14.9814 0Z"
                    fill={`${selectedFilterTagsListStore?.length ? "white" : "#424242"}`}
                  />
                </svg>
              </button>
            )}
            <NavOptions />
          </div>
        </div>
      )}

      {verFijadas && pinnedSubjectsList.length > 0 ? (
        <div className="flex flex-col gap-4 flex-grow overflow-hidden min-h-0">
          <div className="flex-shrink-0">
            <OfferOnProcess subjects={subjectsInSearch} />
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            {pinnedSubjectsContent}
          </div>
        </div>
      ) : (
        <div className="flex-grow flex flex-col overflow-hidden" onScroll={handleScroll} ref={scrollTargetRef}>
          {scrollPositionVirtualList > 100 && (
            <SlIcon
              name="box-arrow-in-up"
              onClick={scrollVirtualListToTop}
              className="z-10 p-2 right-[10px] bottom-[10px] bg-[#656464] rounded-full absolute cursor-pointer text-white"
              style={mobileFeatures ? { bottom: "80px" } : {}}
            ></SlIcon>
          )}
          {offeredSubjects === null && (
            <div className="flex flex-col gap-4 skeleton">
              <SlSkeleton effect="pulse" className="h-[200px]" />
              <SlSkeleton effect="pulse" className="h-[200px]" />
              <SlSkeleton effect="pulse" className="h-[200px]" />
              <style>{css}</style>
            </div>
          )}
          <OfferOnProcess subjects={subjectsInSearch} />
          <OfferSubjectsVirtualizedList
            subjects={subjectsInSearch}
            virtualListRef={virtualListRef}
            handleScroll={handleScrollVirtualList}
          />
        </div>
      )}
      {/* </div> */}
    </div>
  );
}
